import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HeroAPIs } from '@hero/api';

/**
 * Logging metadata for API calls (internal use only)
 * This is stripped from the request before sending to the API
 */
export interface APILoggingMetadata {
  /** The full API name (e.g., "order.getOne") */
  apiName: HeroAPIs;
  /** The function name extracted from API name (e.g., "getOne") */
  funcName: string;
}

/**
 * Store for logging metadata using WeakMap
 * Uses WeakMap with config object as key - safe for concurrent requests because:
 * 1. Each request creates a new unique config object
 * 2. WeakMap uses object identity (reference) as the key
 * 3. The same config object is used throughout the request lifecycle (including interceptors)
 */
export type LoggingMetadataStore = WeakMap<AxiosRequestConfig, APILoggingMetadata>;

/**
 * Parse request data - handles JSON objects, JSON strings, and query strings
 */
function parseRequestData(data: any): any {
  if (!data) return data;

  // If it's already an object, return as-is
  if (typeof data === 'object' && !(data instanceof FormData)) {
    return data;
  }

  // If it's a string, try to parse it
  if (typeof data === 'string') {
    // Try parsing as JSON first (most common case)
    if (data.trim().startsWith('{') || data.trim().startsWith('[')) {
      try {
        return JSON.parse(data);
      } catch {
        // Not valid JSON, continue to query string check
      }
    }

    // Try parsing as query string if it contains '='
    if (data.includes('=')) {
      try {
        const parsed: Record<string, string> = {};
        data.split('&').forEach((pair: string) => {
          const [key, ...rest] = pair.split('=');
          if (key) {
            parsed[decodeURIComponent(key)] = decodeURIComponent(rest.join('='));
          }
        });
        return parsed;
      } catch {
        return data;
      }
    }

    // Return as-is if it's just a plain string
    return data;
  }

  // Return as-is for FormData and other types
  return data;
}

/**
 * Extract query parameters from URL as key-value pairs
 */
export function extractQueryParams(url: string): Record<string, string> {
  try {
    const queryString = url.includes('?') ? url.split('?')[1] : '';
    if (!queryString) return {};

    const params: Record<string, string> = {};
    queryString.split('&').forEach(pair => {
      const [key, ...rest] = pair.split('=');
      if (key) {
        params[decodeURIComponent(key)] = decodeURIComponent(rest.join('='));
      }
    });
    return params;
  } catch {
    return {};
  }
}

/**
 * Log API request and response together
 */
export function logRequestResponse(
  response: AxiosResponse,
  loggingMetadataStore: LoggingMetadataStore,
): void {
  const config = response.config;
  const method = config.method?.toUpperCase() || 'UNKNOWN';
  const url = config.url || '';
  const fullUrl = url.startsWith('http') ? url : config.baseURL ? `${config.baseURL}${url}` : url;
  const status = response.status;
  const statusIcon = status >= 200 && status < 300 ? '✅' : '⚠️';

  // Get logging metadata from WeakMap
  const logging = loggingMetadataStore.get(config);
  const apiName = logging?.apiName;
  const title = apiName
    ? `${statusIcon} [API] ${apiName} - ${method} ${status}`
    : `${statusIcon} [API] ${method} ${status} ${url} - ${status}`;

  const isBlobResponse = response.data instanceof Blob || response.config.responseType === 'blob';
  const queryParams = config.params || extractQueryParams(fullUrl);
  const reqData = config.data ? parseRequestData(config.data) : null;

  const safeHeaders: Record<string, any> = {};
  if (config.headers) {
    Object.assign(safeHeaders, config.headers);
    if (safeHeaders['Authorization']) {
      safeHeaders['Authorization'] = 'Bearer ***';
    }
  }

  const resData = isBlobResponse
    ? `[Blob] ${(response.data as Blob)?.size || 0} bytes`
    : response.data?.data;

  const details: Record<string, any> = {
    url: fullUrl,
    fullResponse: response.data,
    headers: safeHeaders,
  };
  if (isBlobResponse) {
    details['contentType'] = response.headers['content-type'];
  }

  console.groupCollapsed(title);
  console.log('RES:', resData);
  if (reqData) {
    console.log('REQ:', reqData);
  }
  if (Object.keys(queryParams).length > 0) {
    console.log('Params:', queryParams);
  }
  console.log('DETAILS:', details);
  console.groupEnd();
}

/**
 * Log API error
 */
export function logError(error: any, loggingMetadataStore: LoggingMetadataStore): void {
  const config = error.config;
  const method = config?.method?.toUpperCase() || 'UNKNOWN';
  const url = config?.url || '';
  const fullUrl = url.startsWith('http') ? url : config?.baseURL ? `${config.baseURL}${url}` : url;
  const status = error.response?.status || 'N/A';
  const message = error.response?.data?.message || error.message || 'Unknown error';

  // Get logging metadata from WeakMap
  const logging = config ? loggingMetadataStore.get(config) : undefined;
  const apiName = logging?.apiName;

  const title = apiName
    ? `❌ [SDK] ${apiName} - ${method} ${url} - ${status}`
    : `❌ [SDK] ${method} ${url} - ${status}`;

  console.group(title);
  console.log('URL:', fullUrl);

  // Extract and display query params from URL or config
  const queryParams = config?.params || extractQueryParams(fullUrl);
  if (Object.keys(queryParams).length > 0) {
    console.table(queryParams);
  }

  if (config?.data) {
    console.log('Body:', parseRequestData(config.data));
  }
  console.error('Status:', status);
  console.error('Error:', message);
  if (error.response?.data) {
    console.error('Error Data:', error.response.data);
  }
  console.groupEnd();
}

/** Metadata shared by agent SSE stream log helpers. */
export interface AgentStreamLogContext {
  apiName: HeroAPIs;
  method?: string;
  url: string;
  req?: unknown;
}

/**
 * Log the start of an agent SSE stream (dev/staging only — gated by
 * `enableLogs` at the call site).
 */
export function logAgentStreamStart(ctx: AgentStreamLogContext): void {
  const method = ctx.method?.toUpperCase() ?? 'POST';
  const title = `🔄 [API] ${ctx.apiName} - ${method} (SSE stream)`;

  console.groupCollapsed(title);
  console.log('URL:', ctx.url);
  if (ctx.req) {
    console.log('REQ:', ctx.req);
  }
  console.groupEnd();
}

/**
 * HTTP failure before the SSE body is readable — mirrors `logError`.
 */
export function logAgentStreamHttpError(
  ctx: AgentStreamLogContext & {
    status: number;
    message: string;
    errorData?: unknown;
  },
): void {
  const method = ctx.method?.toUpperCase() ?? 'POST';
  const title = `❌ [SDK] ${ctx.apiName} - ${method} ${ctx.url} - ${ctx.status}`;

  console.group(title);
  console.log('URL:', ctx.url);
  if (ctx.req) {
    console.log('Body:', ctx.req);
  }
  console.error('Status:', ctx.status);
  console.error('Error:', ctx.message);
  if (ctx.errorData) {
    console.error('Error Data:', ctx.errorData);
  }
  console.groupEnd();
}

/**
 * In-stream SSE `error` event — HTTP was 200 but the agent turn failed.
 */
export function logAgentStreamEventError(
  ctx: AgentStreamLogContext & {
    code?: string;
    message: string;
    referenceId?: string;
    runId?: string;
  },
): void {
  const method = ctx.method?.toUpperCase() ?? 'POST';
  const title = `❌ [SDK] ${ctx.apiName} - ${method} 200 (stream error)`;

  console.group(title);
  console.log('URL:', ctx.url);
  if (ctx.req) {
    console.log('REQ:', ctx.req);
  }
  if (ctx.code) {
    console.error('Code:', ctx.code);
  }
  console.error('Error:', ctx.message);
  if (ctx.referenceId) {
    console.error('Reference:', ctx.referenceId);
  }
  if (ctx.runId) {
    console.log('Run:', ctx.runId);
  }
  console.groupEnd();
}

/**
 * Terminal SSE `run_finished` event — success or failure summary.
 */
export function logAgentStreamFinished(
  ctx: AgentStreamLogContext & {
    status: string;
    runId?: string;
  },
): void {
  const method = ctx.method?.toUpperCase() ?? 'POST';
  const ok = ctx.status === 'completed';
  const statusIcon = ok ? '✅' : '⚠️';
  const title = `${statusIcon} [API] ${ctx.apiName} - ${method} (stream ${ctx.status})`;

  console.groupCollapsed(title);
  console.log('URL:', ctx.url);
  if (ctx.runId) {
    console.log('Run:', ctx.runId);
  }
  console.groupEnd();
}
